import AddRounded from '@mui/icons-material/AddRounded';
import Edit from '@mui/icons-material/Edit';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import RestoreRounded from '@mui/icons-material/RestoreRounded';
import CircularProgress from '@mui/material/CircularProgress';
import type { GridColDef } from '@mui/x-data-grid-pro';
import type { ClientList } from '@src/clients/domain/Client';
import { useArchiveClient } from '@src/clients/hooks/useArchiveClient';
import { useGetActiveOrdersForBillToClient } from '@src/clients/hooks/useGetActiveOrderForBillToClient';
import { useGetActiveOrdersForClient } from '@src/clients/hooks/useGetActiveOrdersForClient';
import { useGetNewOrdersForBillToClient } from '@src/clients/hooks/useGetNewOrdersForBillToClient';
import { useGetNewOrdersForClient } from '@src/clients/hooks/useGetNewOrdersForClient';
import { useListAllWithClientAsBillTo } from '@src/clients/hooks/useListAllWithClientAsBillTo';
import { useUnarchiveClient } from '@src/clients/hooks/useUnarchiveClient';
import { AddAgencyClientMenuButton } from '@src/clientUser/agency/AddAgencyClientMenuButton';
import type { Agency } from '@src/clientUser/agency/Agency';
import { useIsClientUser } from '@src/clientUser/useClientUser';
import { BaseModal } from '@src/common/components/BaseModal';
import { ButtonLink } from '@src/common/components/button/ButtonLink';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { StatusBadge } from '@src/common/components/StatusBadge';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { theme } from '@src/common/styles/Theme';
import { colors } from '@src/common/styles/valueTokens';
import { ClientsNavigation } from '@src/routing/ClientsNavigation';
import { useId, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface ManageClientsProps {
  addClientPageLink?: string;
  addClientAgencies?: Agency[];
  clientList: ClientList;
  canArchive?: boolean;
}

export const ManageClients = ({
  addClientPageLink,
  addClientAgencies = [],
  clientList,
  canArchive = false,
}: ManageClientsProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const listId = useId();
  const isClientUser = useIsClientUser();

  const addClientLink =
    addClientPageLink ??
    (addClientAgencies.length === 1
      ? ClientsNavigation.addAgencyClient.url(addClientAgencies[0].id)
      : undefined);

  const navigateToClientPage = (clientId: string) => {
    navigate(ClientsNavigation.get.url(clientId));
  };

  const baseColumns: GridColDef[] = [
    { field: 'client', headerName: t('client'), flex: 1 },
    {
      field: 'doingBusinessAs',
      headerName: t('fields.doingBusinessAs'),
      flex: 1,
    },
    {
      field: 'type',
      headerName: t('clientType'),
      flex: 1,
      renderCell: (params) => {
        if (params.value) {
          return <AgencyBadge />;
        }
        return null;
      },
    },
  ];

  const columns: GridColDef[] = [
    ...baseColumns,
    { field: 'contact', headerName: t('contact'), flex: 1 },
    { field: 'email', headerName: t('email'), flex: 1 },
    { field: 'phone', headerName: t('phone'), flex: 1 },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        return (
          <div>
            {!params.row.isArchived && (
              <IconButton
                onClick={() => navigateToClientPage(params.row.id)}
                title={t('edit')}
              >
                <Edit />
              </IconButton>
            )}
            {canArchive &&
              (params.row.isArchived ? (
                <UnarchiveClientButton clientId={params.row.id} />
              ) : (
                <ArchiveClientButton clientId={params.row.id} />
              ))}
          </div>
        );
      },
    },
  ];

  const rows = clientList.clients.map((client) => {
    return {
      id: client.id,
      client: client.name,
      doingBusinessAs: client.doingBusinessAs ?? '-',
      type: client.isAgency(),
      contact: client.primaryContact.name,
      email: client.primaryContact.email,
      phone: client.primaryContact.phone,
      isArchived: client.isArchived(),
    };
  });

  return (
    <PageLayout title={t('clients')} id={listId}>
      <FilterableTable
        ariaLabel="clients-list"
        columns={columns}
        rows={rows}
        rightHeaderBlock={
          !isClientUser ? (
            <>
              {addClientLink != undefined && (
                <ButtonLink to={addClientLink} startIcon={<AddRounded />}>
                  {t('addClient')}
                </ButtonLink>
              )}
            </>
          ) : addClientAgencies.length > 1 ? (
            <AddAgencyClientMenuButton agencies={addClientAgencies} />
          ) : (
            <>
              {addClientLink != undefined && (
                <ButtonLink to={addClientLink} startIcon={<AddRounded />}>
                  {t('addClient')}
                </ButtonLink>
              )}
            </>
          )
        }
        paginationAndSortMode="client"
      />
    </PageLayout>
  );
};

const AgencyBadge = () => {
  const { t } = useTranslation();
  return (
    <StatusBadge
      size="medium"
      text={t('agency')}
      color={colors.neutral[0]}
      borderColor={colors.neutral[0]}
      backgroundColor={colors.neutral[100]}
      radiusFull
    />
  );
};

const UnarchiveClientButton = ({ clientId }: { clientId: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const { unarchiveClient } = useUnarchiveClient({
    clientId,
    onSuccess: () => {
      setOpen(false);
      showSuccessToast(t('unarchiveClient.success'));
    },
    onError: () => {
      showErrorToast(t('unarchiveClient.error'));
    },
  });

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        title={t('unarchiveClient.title')}
      >
        <RestoreRounded color="secondary" />
      </IconButton>
      <BaseModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('unarchiveClient.title')}
      >
        <Stack gap={theme.spacing['3']}>
          <Typography>{t('unarchiveClient.message')}</Typography>
          <CloseConfirmModalFooter
            onClose={() => setOpen(false)}
            confirm={() => unarchiveClient()}
            closeLabel={t('cancel')}
            confirmLabel={t('unarchiveClient.confirm')}
          />
        </Stack>
      </BaseModal>
    </>
  );
};

const ArchiveClientButton = ({ clientId }: { clientId: string }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const { archiveClient } = useArchiveClient({
    clientId,
    onSuccess: () => {
      setOpen(false);
      showSuccessToast(t('archiveClient.success'));
    },
    onError: () => {
      showErrorToast(t('archiveClient.error'));
    },
  });

  const {
    hasActiveOrders,
    activeOrdersCount,
    isLoading: activeOrdersForClientIsLoading,
  } = useGetActiveOrdersForClient(clientId, open);
  const {
    hasNewOrders,
    newOrdersCount,
    isLoading: newOrdersForClientIsLoading,
  } = useGetNewOrdersForClient(clientId, open);
  const {
    hasNewOrdersForBillToClient,
    newOrdersForBillToClientCount,
    isLoading: newOrdersForBillToIsLoading,
  } = useGetNewOrdersForBillToClient(clientId, open);
  const {
    hasActiveOrdersForBillToClient,
    activeOrdersForBillToClientCount,
    isLoading: activeOrdersForBillToIsLoading,
  } = useGetActiveOrdersForBillToClient(clientId, open);

  const isLoadingOrderCounts =
    activeOrdersForClientIsLoading ||
    newOrdersForClientIsLoading ||
    newOrdersForBillToIsLoading ||
    activeOrdersForBillToIsLoading;

  const handleCloseModal = () => setOpen(false);

  return (
    <>
      <IconButton
        onClick={() => setOpen(true)}
        title={t('archiveClient.title')}
      >
        <PersonOffOutlinedIcon />
      </IconButton>

      {isLoadingOrderCounts ? (
        <BaseModal
          open={open}
          onClose={handleCloseModal}
          title={t('archiveClient.title')}
        >
          <CircularProgress />
        </BaseModal>
      ) : hasActiveOrders ||
        hasNewOrders ||
        hasActiveOrdersForBillToClient ||
        hasNewOrdersForBillToClient ? (
        <BlockedArchiveClientModal
          open={open}
          onClose={handleCloseModal}
          activeOrdersCount={activeOrdersCount}
          newOrdersCount={newOrdersCount}
          billToActiveOrdersCount={activeOrdersForBillToClientCount}
          billToNewOrdersCount={newOrdersForBillToClientCount}
        />
      ) : (
        <ArchiveClientConfirmModal
          key={clientId}
          open={open}
          onClose={handleCloseModal}
          onConfirm={archiveClient}
          clientId={clientId}
        />
      )}
    </>
  );
};

interface ArchiveClientConfirmModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  clientId: string;
}

const ArchiveClientConfirmModal = ({
  open,
  onClose,
  onConfirm,
  clientId,
}: ArchiveClientConfirmModalProps) => {
  const { t } = useTranslation();
  const { clientAsBillToList } = useListAllWithClientAsBillTo(clientId, open);

  return (
    <BaseModal open={open} onClose={onClose} title={t('archiveClient.title')}>
      <Stack gap={theme.spacing['3']}>
        {clientAsBillToList?.clients.length > 0 && (
          <Typography>
            <>
              {t('archiveClient.clientsWithBillTo')}
              <br />
              {clientAsBillToList.clients.map((client) => (
                <div key={client.id} style={{ paddingLeft: '2rem' }}>
                  {client.name}
                </div>
              ))}
            </>
          </Typography>
        )}
        <Typography>{t('archiveClient.message')}</Typography>
        <CloseConfirmModalFooter
          onClose={onClose}
          confirm={onConfirm}
          closeLabel={t('cancel')}
          confirmLabel={t('archiveClient.confirm')}
        />
      </Stack>
    </BaseModal>
  );
};

interface BlockedArchiveClientModalProps {
  open: boolean;
  onClose: () => void;
  activeOrdersCount: number;
  newOrdersCount: number;
  billToActiveOrdersCount: number;
  billToNewOrdersCount: number;
}

const BlockedArchiveClientModal = ({
  open,
  onClose,
  activeOrdersCount,
  newOrdersCount,
  billToActiveOrdersCount,
  billToNewOrdersCount,
}: BlockedArchiveClientModalProps) => {
  const { t } = useTranslation();

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={t('archiveClient.blockedTitle')}
    >
      <Stack gap={theme.spacing['3']}>
        <Typography>
          <Trans
            i18nKey="archiveClient.blocked"
            values={{
              activeCount: activeOrdersCount,
              newCount: newOrdersCount,
              billToActiveCount: billToActiveOrdersCount,
              billToNewCount: billToNewOrdersCount,
            }}
            components={{
              strong: <strong style={{ fontWeight: 600 }} />,
              br: <br />,
            }}
          />
        </Typography>
      </Stack>
    </BaseModal>
  );
};
